import type { AiDifficulty, ConstructedDeck, GameCommand, RoomSnapshot, RoomSummary } from "@soccertcg/game-engine";

export interface Identity { userId: string; token: string }
export interface LobbyData { waiting: RoomSummary[]; active: RoomSummary[]; history: RoomSummary[] }

const key = (roomId: string) => `soccertcg:${roomId}`;
export const CUSTOM_DECK_KEY = "soccertcg:custom-deck";

export function customDeck(): ConstructedDeck | undefined {
  try { const value = localStorage.getItem(CUSTOM_DECK_KEY); return value ? JSON.parse(value) as ConstructedDeck : undefined; }
  catch { return undefined; }
}

export function saveCustomDeck(deck: ConstructedDeck) { localStorage.setItem(CUSTOM_DECK_KEY, JSON.stringify(deck)); }

export function identity(roomId: string): Identity | undefined {
  try {
    const value = localStorage.getItem(key(roomId));
    return value ? JSON.parse(value) as Identity : undefined;
  } catch { return undefined; }
}

function saveIdentity(roomId: string, value: Identity) {
  localStorage.setItem(key(roomId), JSON.stringify(value));
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { ...init, headers: { "content-type": "application/json", ...init?.headers } });
  const data = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(data.error ?? `Request failed (${response.status})`);
  return data;
}

const authQuery = (roomId: string) => {
  const auth = identity(roomId);
  return auth ? `?userId=${encodeURIComponent(auth.userId)}&token=${encodeURIComponent(auth.token)}` : "";
};

export const api = {
  lobby: () => request<LobbyData>("/api/rooms"),
  async create(roomName: string, playerName: string, starterDeckId: string, aiDifficulty?: AiDifficulty) {
    const selected = starterDeckId === "custom" ? customDeck() : undefined;
    if (starterDeckId === "custom" && !selected) throw new Error("Build and save a custom deck first.");
    const result = await request<{ roomId: string } & Identity>("/api/rooms", { method: "POST", body: JSON.stringify({ roomName, playerName, starterDeckId, ...(selected ? { customDeck: selected } : {}), ...(aiDifficulty ? { aiDifficulty } : {}) }) });
    saveIdentity(result.roomId, result);
    return result;
  },
  async join(roomId: string, playerName: string, starterDeckId: string) {
    const selected = starterDeckId === "custom" ? customDeck() : undefined;
    if (starterDeckId === "custom" && !selected) throw new Error("Build and save a custom deck first.");
    const result = await request<Identity>(`/api/rooms/${roomId}/join`, { method: "POST", body: JSON.stringify({ playerName, starterDeckId, ...(selected ? { customDeck: selected } : {}) }) });
    saveIdentity(roomId, result);
    return result;
  },
  room: (roomId: string) => request<RoomSnapshot>(`/api/rooms/${roomId}${authQuery(roomId)}`),
  command: (roomId: string, command: GameCommand) => request<{ summary: string }>(`/api/rooms/${roomId}/command${authQuery(roomId)}`, { method: "POST", body: JSON.stringify(command) }),
  chat: (roomId: string, message: string, author: string) => request<{ ok: boolean }>(`/api/rooms/${roomId}/chat${authQuery(roomId)}`, { method: "POST", body: JSON.stringify({ message, author }) }),
  socket(roomId: string) {
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    return new WebSocket(`${protocol}//${location.host}/api/rooms/${roomId}/ws${authQuery(roomId)}`);
  }
};
